import React, { memo } from 'react';
import PropTypes from 'prop-types';

import MuiButton from '@mui/material/Button';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

const StyledButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: '8px',
  height: '40px',
  padding: '8px 12px',
  minWidth: '120px',
  fontSize: '14px',
  textTransform: 'capitalize',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.main,
  },
  '&:active': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  '&:focus': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  '&:disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    borderColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}));

const StyledCancelButton = styled(MuiButton)(({ theme }) => ({
  padding: '4px 0',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  '&:hover': {},
  '&:active': {},
  '&:focus': {},
}));

const StyledSecondaryButton = styled(MuiButton)(({ theme }) => ({
  borderRadius: '8px',
  height: '40px',
  padding: '8px 12px',
  minWidth: '120px',
  fontSize: '14px',
  textTransform: 'capitalize',
  background: 'var(--color-surface)',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary
}));

const Button = ({ type, text, color, onClick, disabled, loading, outlined, icon, ...props }) => {
  switch (type) {
    case 'submit':
      return (
        <StyledButton onClick={onClick} color={color || 'primary'} disabled={disabled} type="submit" {...props}>
          {text}
          {loading && <CircularProgress size={24} className="button-processing-indicator" />}
        </StyledButton>
      );

    case 'cancel':
      return <StyledCancelButton onClick={onClick} {...props}>{text || 'Cancel'}</StyledCancelButton>;

    case 'secondary':
      return (
        <StyledSecondaryButton
          onClick={onClick}
          color={color || 'primary'}
          variant="outlined"
          disabled={disabled}
          {...props}
        >
          {!!icon && (
            <Box
              sx={(theme) => ({
                mr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.text.secondary,
              })}
            >
              {icon}
            </Box>
          )}
          {text}
          {loading && <CircularProgress size={24} className="button-processing-indicator" />}
        </StyledSecondaryButton>
      );

    default:
      return (
        <StyledButton
          onClick={onClick}
          color={color || 'primary'}
          variant={outlined ? 'outlined' : 'text'}
          disabled={disabled}
          {...props}
        >
          {!!icon && (
            <Box
              sx={(theme) => ({
                mr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.light,
              })}
            >
              {icon}
            </Box>
          )}
          {text}
          {loading && <CircularProgress size={24} className="button-processing-indicator" />}
        </StyledButton>
      );
  }
};

Button.propTypes = {
  text: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  outlined: PropTypes.bool,
  type: PropTypes.oneOf(['submit', 'cancel', 'secondary']),
};

export default memo(Button);
